import { useMemo } from 'react';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import type { User } from '@alltrails/types/User';
import useUser from './useUser';

type HasFeatureEntitlementArgs = {
  entitlement: FeatureEntitlement;
};

export const hasFeatureEntitlement = (user: User | null, entitlement: FeatureEntitlement) => {
  // Temporary hack while transitioning towards granular conditions entitlements from the 'advanced_conditions' entitlement.
  if (CONDITIONS_FEATURE_ENTITLEMENTS.includes(entitlement)) {
    // If a feature uses a new conditions entitlement like 'temperature' we will allow a fallback to the 'advanced_conditions' entitlement for now.
    return user?.featureEntitlements?.includes(entitlement) || hasFeatureEntitlement(user, 'advanced_conditions');
  }

  return Boolean(user?.featureEntitlements?.includes(entitlement));
};

export const hasEveryFeatureEntitlement = (user: User | null, entitlements: FeatureEntitlement[]) => {
  return entitlements.every(entitlement => hasFeatureEntitlement(user, entitlement));
};

export const CONDITIONS_FEATURE_ENTITLEMENTS: FeatureEntitlement[] = ['precipitation', 'temperature', 'ground_conditions', 'snow_depth'];

export default function useFeatureEntitlements() {
  const user = useUser();

  return useMemo(() => {
    return {
      hasFeatureEntitlement: ({ entitlement }: HasFeatureEntitlementArgs) => {
        return hasFeatureEntitlement(user, entitlement);
      },
      hasEveryFeatureEntitlement: ({ entitlements }: { entitlements: FeatureEntitlement[] }) => {
        return entitlements.every(entitlement => hasFeatureEntitlement(user, entitlement));
      }
    };
  }, [user]);
}
